import React, { useEffect, useMemo, useRef, useState } from "react";
import { Cancel, FileUploadOutlined, Close } from "@mui/icons-material";
import {
  Box,
  Dialog,
  Divider,
  IconButton,
  Slide,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { TransitionProps } from "@mui/material/transitions";
import Dropzone, { ExtendedFile } from "../../common/Dropzone";
import { useForm } from "react-hook-form";
import CustomDropdownField from "../../common/CustomDropdownFiled";
import {
  pdf, doc, docx, jpeg, png, xls, xlsx, txt, csv, gif, bmp, tiff,
} from "../../../constants/applicationConstants";
import Button from "../../common/Button";
import { uploadIVRDocument, fetchIVRDocuments, removeIVRDocument, IVRDocumentInfo, updatePatientInfoDocuments, UpdatePatientDocumentInfo, IVRNotes, uploadIVRNotes, fetchIVRNotesById } from "../../../redux/slice/ivr/ivrDocumentSlice";
import { showHideLoadingDialog, setSingleAlertObj, SingleAlertInfo } from "../../../redux/slice/commonSlice";
import { useAppDispatch } from "../../../redux/slice/redux-hooks";
import {
  CodeTable,
  getCodeTableValuesByTableName,
  useCodetablesSlice,
} from "../../../redux/slice/codetableSlice";
import CustomTextField from "../../common/CustomTextField";
import InfoPopup from "../../common/InfoPopup";
import { useModalStyles } from "../../common/useModalStyles";

interface DocumentsUploadProps {
  open: boolean;
  onClose: () => void;
  onDocumentSelected: (file: Attachment, documentType: string) => void;
  ivrId: number;
  patientId: number;
  onUploadSuccess: () => void;
  hasSecondaryInsurance: boolean;
}

interface Attachment {
  documentName: string;
  content: string;
  fileType: string;
}

type Option = {
  label: string;
  value: string | number;
};

const Transition = React.forwardRef(function Transition(
  props: TransitionProps & { children: React.ReactElement<any, any> },
  ref: React.Ref<unknown>
) {
  return <Slide direction="down" ref={ref} {...props} />;
});

const readFileAsBase64 = (file: File): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = (error) => reject(error);
    reader.readAsDataURL(file);
  });

const IVRDocumentsUploadPopup: React.FC<DocumentsUploadProps> = ({
  open,
  onClose,
  onDocumentSelected,
  ivrId,
  patientId,
  onUploadSuccess,
  hasSecondaryInsurance,
}) => {
  const {
    control,
    handleSubmit,
    setValue,
    getValues,
    clearErrors,
    trigger,
    watch,
    formState: { errors },
  } = useForm();

  const dispatch = useAppDispatch();
  const documentIdsRef = useRef<number[]>([]);
  const [uploadedDocumentIds, setUploadedDocumentIds] = useState<number[]>([]);
  const [uploadedDocumentType, setUploadedDocumentType] = useState<string>("");
  const { allCodeTablesValuesMap, allCodeTableValueFetchStatus } = useCodetablesSlice();
  const [documentTypes, setDocumentTypes] = useState<CodeTable[]>([]);
  const [showSecondaryInsuranceMissing, setShowSecondaryInsuranceMissing] = useState<boolean>(false);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const {
    tokens,
    paperSx,
    headerSx,
    iconBoxSx,
    bodySx,
    footerSx,
    closeIconSx,
    dropdownWrapSx,
    primaryBtnSx,
    cancelBtnSx,
  } = useModalStyles();

  const showNoteField = watch("documentType");

  // ── Effects ──────────────────────────────────────────────────────────────

  useEffect(() => {
    if (watch("documentType") === "SECONDARY_INSURANCE") {
      setShowSecondaryInsuranceMissing(!hasSecondaryInsurance);
    } else {
      setShowSecondaryInsuranceMissing(false);
    }
  }, [watch("documentType"), hasSecondaryInsurance]);

  useEffect(() => {
    if (allCodeTableValueFetchStatus === "success" && allCodeTablesValuesMap) {
      const sortedDocumentTypes = [
        ...getCodeTableValuesByTableName(allCodeTablesValuesMap, "documentType"),
      ];
      setDocumentTypes(sortedDocumentTypes);
    }
  }, [allCodeTablesValuesMap, allCodeTableValueFetchStatus]);

  // ── Handlers ─────────────────────────────────────────────────────────────

  const removeDataUriPrefix = (dataUri: string): string =>
    dataUri.split(",")[1] || "";

  const handleDropzoneChange = async (uploadedFiles: ExtendedFile[]) => {
    if (uploadedFiles && uploadedFiles.length > 0) {
      clearErrors("attachmentIVRDocument");
    } else {
      trigger("attachmentIVRDocument");
      return;
    }

    const fileNames = uploadedFiles.map((f) => f.name);
    const attachments: Attachment[] = await Promise.all(
      uploadedFiles.map(async (file) => {
        const base64Content = await readFileAsBase64(file);
        return {
          documentName: file.name,
          content: removeDataUriPrefix(base64Content),
          fileType: file.type,
        };
      })
    );

    setValue("attachmentIVRDocument", attachments);
    const documentType = getValues("documentType");
    setUploadedDocumentType(documentType);

    if (documentType && documentType !== "OTHER") {
      try {
        dispatch(showHideLoadingDialog(true));
        const documentIds: number[] = [];

        for (let i = 0; i < uploadedFiles.length; i++) {
          const file = uploadedFiles[i];
          const base64Content = await readFileAsBase64(file);
          const documentObject: IVRDocumentInfo = {
            ivrId,
            documentName: fileNames[i],
            content: removeDataUriPrefix(base64Content),
            type: "IVR",
            fileType: file.type,
            documentType,
          };
          const response = await dispatch(uploadIVRDocument(documentObject));
          if (response.payload?.data?.documentId) {
            const documentId = response.payload.data.documentId;
            (file as any).documentId = documentId;
            documentIds.push(documentId);
          }
        }

        documentIdsRef.current = [...documentIdsRef.current, ...documentIds];
        setUploadedDocumentIds(documentIdsRef.current);
      } catch (error) {
        console.error("Error uploading document:", error);
        dispatch(setSingleAlertObj({ message: "Failed to upload document.", alertType: "error" }));
      } finally {
        dispatch(showHideLoadingDialog(false));
      }
    }
  };

  const handleDeleteDocument = async (documentId: number) => {
    if (!documentId) {
      setValue("attachmentIVRDocument", []);
      return;
    }
    try {
      const response = await dispatch(removeIVRDocument(documentId.toString()));
      if (response.payload.code === "V3OK") {
        documentIdsRef.current = documentIdsRef.current.filter((id) => id !== documentId);
        setValue("attachmentIVRDocument", []);
      }
    } catch (error) {
      console.error("Error deleting document:", error);
      dispatch(setSingleAlertObj({ message: "Failed to delete document.", alertType: "error" }));
    }
  };

  const handleDirectNoteSubmit = async (data: IVRNotes) => {
    try {
      const response = await dispatch(uploadIVRNotes(data));
      if (response.payload?.message === "Note is added successfully") {
        const alertObj: SingleAlertInfo = { message: "Note saved Successfully!", alertType: "success" };
        dispatch(setSingleAlertObj(alertObj));
        dispatch(fetchIVRNotesById(ivrId));
      }
    } catch (error) {
      console.log("Error saving note:", error);
    }
  };

  const handleUpdatePatientInfoDocument = async () => {
    try {
      const updatePatientInfoDocsObject: UpdatePatientDocumentInfo = {
        ivrId,
        documentType: uploadedDocumentType,
        patientId,
        documentIds: [uploadedDocumentIds[uploadedDocumentIds.length - 1]],
      };
      await dispatch(updatePatientInfoDocuments(updatePatientInfoDocsObject));
      onUploadSuccess();
    } catch (error) {
      console.error("Error updating patient info document:", error);
    } finally {
      dispatch(fetchIVRDocuments({ ivrId, patientId }));
    }
  };

  const onSubmit = async (data: any) => {
    const attachments = getValues("attachmentIVRDocument");

    if (attachments && attachments.length > 0) {
      const noteObject: IVRNotes = {
        attachmentId: uploadedDocumentIds[uploadedDocumentIds.length - 1],
        noteType: "NOTE",
        content: getValues("content"),
        attachment: attachments[0].content,
        documentName: attachments[0].documentName,
        fileType: attachments[0].fileType,
        xrefTable: "IVR",
        xrefId: ivrId.toString(),
      };

      await handleDirectNoteSubmit(noteObject);

      if (data.documentType !== "OTHER") {
        await handleUpdatePatientInfoDocument();
      }
    }
    onClose();
  };

  // ── Memos ─────────────────────────────────────────────────────────────────

  const documentTypeOptions: Option[] = useMemo(() => {
    const filterDocuments = [
      "SUPPORTING_DOCUMENT",
      "FACESHEET",
      "IMAGEOF_WOUNDS",
      "PRIMARY_INSURANCE",
      "SECONDARY_INSURANCE",
      "OTHER",
    ];
    return documentTypes
      .filter((item) => filterDocuments.includes(item.keyName))
      .sort((a, b) => filterDocuments.indexOf(a.keyName) - filterDocuments.indexOf(b.keyName))
      .map((item) => ({ label: item.displayValue, value: item.keyName }));
  }, [documentTypes]);

  // ── Sub-components ────────────────────────────────────────────────────────

  const FieldLabel = ({ label, required = false }: { label: string; required?: boolean }) => (
    <Typography
      sx={{
        fontSize: "0.8rem",
        fontWeight: 600,
        color: tokens.textSecondary,
        mb: 0.7,
        display: "flex",
        alignItems: "center",
        gap: 0.3,
      }}
    >
      {label}
      {required && (
        <Box component="span" sx={{ color: tokens.accent }}>*</Box>
      )}
    </Typography>
  );

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <>
      {showSecondaryInsuranceMissing && (
        <InfoPopup
          open={showSecondaryInsuranceMissing}
          content="Secondary insurance details are not provided for this IVR."
          onClose={() => {
            setShowSecondaryInsuranceMissing(false);
            setValue("documentType", "");
          }}
          severity="info"
          title="INFO"
        />
      )}

      <Dialog
        open={open}
        TransitionComponent={Transition}
        keepMounted
        onClose={onClose}
        maxWidth="sm"
        fullWidth
        fullScreen={isMobile}
        PaperProps={{ sx: paperSx(isMobile) }}
      >
        {/* ── Header ── */}
        <Box sx={headerSx}>
          <Box sx={iconBoxSx(tokens.accent)}>
            <FileUploadOutlined sx={{ color: tokens.accent, fontSize: 20 }} />
          </Box>

          <Box sx={{ flex: 1 }}>
            <Typography
              fontWeight={700}
              sx={{ fontSize: "1.1rem", color: tokens.textPrimary, lineHeight: 1.3 }}
            >
              Upload Documents
            </Typography>
            <Typography
              sx={{ fontSize: "0.8rem", color: tokens.textSecondary, mt: 0.15 }}
            >
              Attach supporting documents for this IVR
            </Typography>
          </Box>

          <IconButton onClick={onClose} size="small" sx={closeIconSx}>
            <Close sx={{ fontSize: "1rem" }} />
          </IconButton>
        </Box>

        {/* ── Body ── */}
        <form onSubmit={handleSubmit(onSubmit)}>
          <Box
            sx={{
              ...bodySx,
              minHeight: 0,
              overflowY: "auto",
              overflowX: "hidden",
              scrollbarGutter: "stable",
              "&::-webkit-scrollbar": { width: 6, height: 6 },
              "&::-webkit-scrollbar-track": { background: "transparent" },
              "&::-webkit-scrollbar-thumb": {
                background: theme.palette.mode === "dark" ? "rgba(255,255,255,0.12)" : "#CBD5E1",
                borderRadius: 4,
              },
            }}
          >
            {/* Document Type */}
            <Box>
              <FieldLabel label="Document Type" required />
              <Box sx={dropdownWrapSx(!!errors.documentType)}>
                <CustomDropdownField
                  name="documentType"
                  control={control}
                  options={documentTypeOptions}
                  label=""
                  sortOptions={false}
                  size="small"
                  placeholderText="Select a document type..."
                  rules={{ required: "Document type is required." }}
                />
              </Box>
            </Box>

            {/* Notes (shown once a type is selected) */}
            {showNoteField && (
              <Box>
                <FieldLabel label="Notes" required />
                <CustomTextField
                  controllerName="content"
                  control={control}
                  label=""
                  size="small"
                  multiline
                  placeholder="Add a note about this document..."
                  rules={{ required: "Notes are required." }}
                />
              </Box>
            )}

            {/* Dropzone */}
            <Box>
              <FieldLabel label="Attachment" required />
              <Dropzone
                name="attachmentIVRDocument"
                onFileChange={() => {}}
                onFileChangeTest={handleDropzoneChange}
                control={control}
                maxFileCount={1}
                rules={{ required: "Attachment is required." }}
                maxFileSize={{ size: 15, unit: "MB" }}
                onDeleteFile={handleDeleteDocument}
                allowedFileTypes={{
                  ...pdf, ...doc, ...docx, ...txt, ...csv,
                  ...jpeg, ...png, ...gif, ...xls, ...xlsx, ...bmp, ...tiff,
                }}
              />
            </Box>

            {/* Info banner */}
            <Box
              sx={{
                px: 1.5,
                py: 1,
                borderRadius: 1,
                mb: 1,
                backgroundColor:
                  theme.palette.mode === "dark"
                    ? "#141720"
                    : "#eef2f6",
                border: `1px solid ${theme.palette.mode === "dark" ? "rgba(255,255,255,0.07)" : "#e5e9ed"}`,
              }}
            >
              <Typography
                variant="body2"
                sx={{ color: tokens.accent, fontWeight: 600, fontSize: "0.78rem" }}
              >
                Supported: PDF, Excel, Word, CSV, Images, and other text-based formats · Max 15 MB
              </Typography>
            </Box>

          </Box>

          <Divider />

          {/* ── Footer ── */}
          <Box sx={footerSx}>
            <Box sx={{ display: "flex", gap: 1.5 }}>
              <Button
                fullWidth
                type="submit"
                variant="contained"
                startIcon={<FileUploadOutlined sx={{ fontSize: "1rem !important" }} />}
                sx={primaryBtnSx}
              >
                Upload
              </Button>
              <Button
                fullWidth
                variant="outlined"
                color="error"
                startIcon={<Cancel sx={{ fontSize: "1rem !important" }} />}
                onClick={onClose}
                sx={cancelBtnSx}
              >
                Cancel
              </Button>
            </Box>
          </Box>
        </form>
      </Dialog>
    </>
  );
};

export default IVRDocumentsUploadPopup;